// Phiếu đáp án: tìm 4 Ô ĐEN ở góc → nắn thẳng → dò ĐƯỜNG KẺ BẢNG → cắt từng ô đáp án.
// Chạy hoàn toàn trên máy (không gửi ảnh đi). Dùng chung cho phiếu web / Word / PDF vì chỉ dựa
// vào 4 ô đen + đường kẻ bảng (các cột của một bảng luôn rộng bằng nhau), không dựa kích thước cố định.
import { warpQuad, rotate90, type Quad } from '../lesson-builder/lib/docScan'
import type { SheetGroup } from './sheetModel'

interface Gray { g: Uint8Array; w: number; h: number; scale: number }

function toGray(canvas: HTMLCanvasElement, maxSide: number): Gray {
  const scale = Math.min(1, maxSide / Math.max(canvas.width, canvas.height))
  const w = Math.max(1, Math.round(canvas.width * scale)), h = Math.max(1, Math.round(canvas.height * scale))
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(canvas, 0, 0, w, h)
  const d = ctx.getImageData(0, 0, w, h).data
  const g = new Uint8Array(w * h)
  for (let p = 0, i = 0; p < w * h; p++, i += 4) g[p] = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) | 0
  return { g, w, h, scale }
}

// Điểm tối so với TRUNG BÌNH CỤC BỘ (ảnh tích phân) → chịu được ảnh chụp sáng không đều, có bóng.
function darkMask({ g, w, h }: Gray, win: number, k: number, cap = 255): Uint8Array {
  const W = w + 1
  const I = new Float64Array(W * (h + 1))
  for (let y = 0; y < h; y++) {
    let row = 0
    for (let x = 0; x < w; x++) { row += g[y * w + x]; I[(y + 1) * W + x + 1] = I[y * W + x + 1] + row }
  }
  const r = win >> 1, m = new Uint8Array(w * h)
  for (let y = 0; y < h; y++) {
    const y0 = Math.max(0, y - r), y1 = Math.min(h, y + r + 1)
    for (let x = 0; x < w; x++) {
      const x0 = Math.max(0, x - r), x1 = Math.min(w, x + r + 1)
      const mean = (I[y1 * W + x1] - I[y0 * W + x1] - I[y1 * W + x0] + I[y0 * W + x0]) / ((x1 - x0) * (y1 - y0))
      const v = g[y * w + x]
      m[y * w + x] = v < mean * k && v < cap ? 1 : 0
    }
  }
  return m
}

interface Blob { cx: number; cy: number; size: number }

// Vùng đen liền khối, gần vuông, đặc (≥ 72% hộp bao) → ứng viên ô đen góc. Được chạm mép ảnh (HS kéo góc
// sát phiếu); nền bàn tối thường to/méo nên đã bị loại bởi cỡ + độ vuông.
function squareBlobs(m: Uint8Array, w: number, h: number): Blob[] {
  const seen = new Uint8Array(w * h), stack = new Int32Array(w * h), out: Blob[] = []
  const maxSide = Math.min(w, h) * 0.12
  for (let start = 0; start < w * h; start++) {
    if (!m[start] || seen[start]) continue
    let top = 0, area = 0, x0 = w, y0 = h, x1 = 0, y1 = 0, sx = 0, sy = 0
    stack[top++] = start; seen[start] = 1
    while (top) {
      const p = stack[--top], x = p % w, y = (p / w) | 0
      area++; sx += x; sy += y
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y
      if (x > 0 && m[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack[top++] = p - 1 }
      if (x < w - 1 && m[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack[top++] = p + 1 }
      if (y > 0 && m[p - w] && !seen[p - w]) { seen[p - w] = 1; stack[top++] = p - w }
      if (y < h - 1 && m[p + w] && !seen[p + w]) { seen[p + w] = 1; stack[top++] = p + w }
    }
    const bw = x1 - x0 + 1, bh = y1 - y0 + 1
    if (bw < 6 || bh < 6 || bw > maxSide || bh > maxSide) continue
    if (bw / bh < 0.6 || bw / bh > 1.6 || area / (bw * bh) < 0.72) continue
    out.push({ cx: sx / area, cy: sy / area, size: Math.sqrt(area) })
  }
  return out
}

const cosAt = (a: Blob, b: Blob, c: Blob) => {     // cos góc tại b
  const ux = a.cx - b.cx, uy = a.cy - b.cy, vx = c.cx - b.cx, vy = c.cy - b.cy
  return (ux * vx + uy * vy) / (Math.hypot(ux, uy) * Math.hypot(vx, vy) || 1)
}

/** Tìm 4 ô đen góc phiếu → Quad chuẩn hoá [TL, TR, BR, BL] (0..1) theo ảnh vào; không thấy → null.
 *  Phiếu nằm ngang (rộng > cao): ảnh chụp dọc thì tự xoay thứ tự góc cho phiếu nằm ngang. */
export function findMarkerQuad(canvas: HTMLCanvasElement): Quad | null {
  const gray = toGray(canvas, 900)
  const win = Math.max(31, Math.round(Math.max(gray.w, gray.h) / 12) | 1)
  // Ảnh tối / mực nhạt / chói sáng: thử vài mức ngưỡng, lấy mức đầu tiên ra 4 ô hợp lệ
  for (const [k, cap] of [[0.6, 150], [0.72, 170], [0.5, 130]] as const) {
    const q = quadFrom(squareBlobs(darkMask(gray, win, k, cap), gray.w, gray.h), gray.w, gray.h)
    if (q) return q
  }
  return null
}

function quadFrom(blobs: Blob[], w: number, h: number): Quad | null {
  if (blobs.length < 4) return null
  // Mỗi góc lấy 3 ứng viên "xa nhất về góc đó", thử mọi tổ hợp, chọn tổ hợp vuông vức + cỡ đều nhất.
  const pick = (score: (b: Blob) => number) => [...blobs].sort((a, b) => score(a) - score(b)).slice(0, 3)
  const tl = pick((b) => b.cx + b.cy), tr = pick((b) => -b.cx + b.cy)
  const br = pick((b) => -b.cx - b.cy), bl = pick((b) => b.cx - b.cy)
  let best: Blob[] | null = null, bestScore = Infinity
  for (const a of tl) for (const b of tr) for (const c of br) for (const d of bl) {
    const q = [a, b, c, d]
    if (new Set(q).size < 4) continue
    const sizes = q.map((x) => x.size), mx = Math.max(...sizes), mn = Math.min(...sizes)
    if (mx / mn > 2.2) continue
    const angle = Math.max(...q.map((x, i) => Math.abs(cosAt(q[(i + 3) % 4], x, q[(i + 1) % 4]))))
    if (angle > 0.45) continue                                  // các góc 63°–117° (chụp xiên vẫn nhận)
    const top = Math.hypot(b.cx - a.cx, b.cy - a.cy), bot = Math.hypot(c.cx - d.cx, c.cy - d.cy)
    const left = Math.hypot(d.cx - a.cx, d.cy - a.cy), right = Math.hypot(c.cx - b.cx, c.cy - b.cy)
    if (Math.min(top, bot) / Math.max(top, bot) < 0.55 || Math.min(left, right) / Math.max(left, right) < 0.55) continue
    if (Math.min(top, left) < 8 * mx) continue                  // 4 ô phải cách xa nhau (không phải chấm QR)
    const score = angle + (mx / mn - 1)
    if (score < bestScore) { bestScore = score; best = q }
  }
  if (!best) return null
  let q = best.map((b) => [b.cx / w, b.cy / h] as [number, number])
  const width = Math.hypot((q[1][0] - q[0][0]) * w, (q[1][1] - q[0][1]) * h)
  const height = Math.hypot((q[3][0] - q[0][0]) * w, (q[3][1] - q[0][1]) * h)
  if (height > width) q = [q[3], q[0], q[1], q[2]]            // ảnh chụp dọc → phiếu nằm ngang
  return q
}

/** Nới quad ra ngoài (từ tâm) để ảnh cắt còn TRỌN 4 ô đen — bước quét sau còn dò lại được. */
export function expandQuad(q: Quad, f = 0.07, clamp = true): Quad {
  const cx = q.reduce((s, p) => s + p[0], 0) / 4, cy = q.reduce((s, p) => s + p[1], 0) / 4
  const c = (v: number) => (clamp ? Math.min(1, Math.max(0, v)) : v)
  return q.map(([x, y]) => [c(cx + (x - cx) * (1 + f)), c(cy + (y - cy) * (1 + f))] as [number, number])
}

export interface SheetSection { group: SheetGroup; count: number; per: number }
export interface SheetCell { group: SheetGroup; index: number; canvas: HTMLCanvasElement; blank: boolean }

// Gom các hàng/cột liên tiếp được đánh dấu thành vị trí đường kẻ (tâm).
function clusters(flags: Uint8Array, gap = 2): number[] {
  const out: number[] = []
  let s = -1, last = -1
  for (let i = 0; i <= flags.length; i++) {
    if (i < flags.length && flags[i]) { if (s < 0) s = i; last = i; continue }
    if (s >= 0 && (i >= flags.length || i - last > gap)) { out.push((s + last) / 2); s = -1 }
  }
  return out
}

function crop(src: HTMLCanvasElement, x: number, y: number, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h))
  c.getContext('2d')!.drawImage(src, x, y, w, h, 0, 0, c.width, c.height)
  return c
}

/** Lý do lần dò bảng gần nhất thất bại (in ra console để chẩn đoán ảnh thật). */
export let lastSheetFailure = ''

function cellsOf(canvas: HTMLCanvasElement, sections: SheetSection[]): SheetCell[] | null {
  const gray = toGray(canvas, 1100)
  const { g, w, h, scale } = gray
  const mL = darkMask(gray, 15, 0.9)            // đường kẻ: nhạy hơn (kiểu đen trắng) cho ảnh xám, mờ, kẻ nhạt
  // Đường kẻ ngang dò ở DẢI GIỮA (25–75% bề rộng — giấy cong thì dải giữa vẫn gần thẳng): độ phủ từng hàng
  // (gộp ±1). Đường kẻ = ĐỈNH HẸP phủ ≥ 30% và nổi hẳn so với 2 bên; chữ in / chữ viết thì loang rộng.
  const sx0 = Math.round(w * 0.25), sx1 = Math.round(w * 0.75)
  const cov = new Float32Array(h)
  for (let y = 1; y < h - 1; y++) {
    let n = 0
    for (let x = sx0; x < sx1; x++) { const p = y * w + x; if (mL[p] || mL[p - w] || mL[p + w]) n++ }
    cov[y] = n / (sx1 - sx0)
  }
  const lines: number[] = []
  const maxH = Math.max(5, Math.round(h * 0.014))
  for (let y = 1; y < h - 1;) {
    if (cov[y] < 0.3) { y++; continue }
    let y1 = y, peak = 0
    while (y1 < h - 1 && cov[y1] >= 0.3) { if (cov[y1] > peak) peak = cov[y1]; y1++ }
    const above = cov[Math.max(0, y - 3)], below = cov[Math.min(h - 1, y1 + 2)]
    if (y1 - y <= maxH && above < peak * 0.55 && below < peak * 0.55) lines.push((y + y1 - 1) / 2)
    y = y1
  }
  const need = sections.reduce((s, sec) => s + 2 * Math.ceil(sec.count / sec.per) + 1, 0)
  if (lines.length < need) { lastSheetFailure = `thấy ${lines.length}/${need} đường kẻ ngang`; return null }

  const rowDark = (y: number, x0: number, x1: number) => { let n = 0; for (let x = x0; x < x1; x++) n += mL[y * w + x]; return n }
  const colDark = (x: number, y0: number, y1: number) => { let n = 0; for (let y = y0; y < y1; y++) n += mL[y * w + x]; return n }
  // Tìm lại cạnh ô quanh vị trí ước lượng (bám theo độ cong của giấy)
  const refineY = (y: number, x0: number, x1: number, r: number) => {
    let best = y, bv = -1
    for (let yy = Math.max(0, Math.round(y - r)); yy <= Math.min(h - 1, Math.round(y + r)); yy++) {
      const v = rowDark(yy, x0, x1); if (v > bv) { bv = v; best = yy }
    }
    return bv >= (x1 - x0) * 0.6 ? best : y
  }
  const refineX = (x: number, y0: number, y1: number, r: number) => {
    let best = x, bv = -1
    for (let xx = Math.max(0, Math.round(x - r)); xx <= Math.min(w - 1, Math.round(x + r)); xx++) {
      const v = colDark(xx, y0, y1); if (v > bv) { bv = v; best = xx }
    }
    return bv >= (y1 - y0) * 0.85 ? best : x       // nét chữ dọc ("1", "B") không chạy hết chiều cao ô
  }

  // Mỗi bảng = (2·số hàng + 1) đường kẻ, khoảng cách xen kẽ: hàng số câu THẤP – hàng đáp án CAO.
  // Giữa các bảng có thể lẫn "đường kẻ giả" (tiêu đề in đậm, dòng chấm "Kinh nghiệm") → tìm lần lượt
  // từng bảng theo mẫu, được bỏ qua đường thừa, quay lui nếu bảng sau không khớp.
  const pattern = (L: number[]) => {
    const g1s: number[] = [], g2s: number[] = []
    for (let r = 0; 2 * r + 2 < L.length; r++) {
      const g1 = L[2 * r + 1] - L[2 * r], g2 = L[2 * r + 2] - L[2 * r + 1]
      if (g1 < h * 0.015 || g2 < g1 * 1.15 || g2 > g1 * 4) return false
      g1s.push(g1); g2s.push(g2)
    }
    const even = (a: number[]) => Math.max(...a) / Math.min(...a) < 1.5
    return even(g1s) && even(g2s)
  }
  // est = chiều cao hàng số câu / hàng đáp án đo ở bảng trước: các bảng cùng cỡ chữ in → bảng sau phải
  // có hàng cao TƯƠNG ĐƯƠNG (chặn ghép nhầm bảng); cũng dùng để suy ra 1 đường kẻ mép bảng bị mờ mất.
  // Lượt 1 chỉ nhận bảng đủ đường kẻ; không được mới cho phép suy ra đường thiếu (lượt 2).
  const similar = (L: number[], est: { g1: number; g2: number } | null) => {
    if (!est) return true
    const g1 = L[1] - L[0], g2 = L[2] - L[1]
    return Math.abs(g1 - est.g1) <= est.g1 * 0.35 && Math.abs(g2 - est.g2) <= est.g2 * 0.35
  }
  const search = (si: number, from: number, est: { g1: number; g2: number } | null, infer: boolean): SheetCell[] | null => {
    if (si === sections.length) return []
    const sec = sections[si]
    const n = 2 * Math.ceil(sec.count / sec.per) + 1
    for (let i = from; i < lines.length; i++) {
      const tries: { L: number[]; used: number }[] = []
      if (i + n <= lines.length) tries.push({ L: lines.slice(i, i + n), used: n })
      if (infer && i + n - 1 <= lines.length) {
        const W = lines.slice(i, i + n - 1)
        const g1 = W.length >= 3 ? W[2] - W[1] : est?.g1, g2 = W.length >= 3 ? W[1] - W[0] : est?.g2
        if (g1 && g2) {
          tries.push({ L: [W[0] - g1, ...W], used: n - 1 })                 // thiếu viền trên
          tries.push({ L: [...W, W[W.length - 1] + g2], used: n - 1 })      // thiếu viền dưới
        }
      }
      for (const t of tries) {
        if (t.L[0] < 0 || t.L[t.L.length - 1] >= h || !pattern(t.L) || !similar(t.L, est)) continue
        const mine = sectionCells(sec, t.L)
        if (!mine) continue
        const rest = search(si + 1, i + t.used, est || { g1: t.L[1] - t.L[0], g2: t.L[2] - t.L[1] }, infer)
        if (rest) return [...mine, ...rest]
      }
    }
    if (!lastSheetFailure.startsWith('bảng')) {
      lastSheetFailure = `bảng ${sec.group}: không khớp mẫu · đường kẻ ${lines.map((v) => v.toFixed(0)).join(',')}`
    }
    return null
  }
  lastSheetFailure = ''
  return search(0, 0, null, false) || search(0, 0, null, true)

  function sectionCells(sec: SheetSection, L: number[]): SheetCell[] | null {
    const out: SheetCell[] = []
    // Cột: độ phủ vạch dọc (±2 cột vì có thể hơi nghiêng) trong dải hàng ghi đáp án đầu. Bảng chia ĐỀU →
    // tìm LƯỚI (mép trái, bề rộng cột) khớp nhất với cả (số cột + 1) vạch: vạch nhạt/đứt vẫn góp điểm,
    // vài nét chữ dọc lẻ tẻ không kéo lệch được cả lưới.
    const y0 = Math.round(L[1]) + 3, y1 = Math.round(L[2]) - 3
    const colScore = new Float32Array(w)
    for (let x = 2; x < w - 2; x++) {
      let n = 0
      for (let y = y0; y <= y1; y++) {
        const p = y * w + x
        if (mL[p] || mL[p - 1] || mL[p - 2] || mL[p + 1] || mL[p + 2]) n++
      }
      colScore[x] = n / (y1 - y0 + 1)
    }
    let best = -1, left = 0, colW = 0
    for (let cw = (w * 0.55) / sec.per; cw <= (w * 0.99) / sec.per; cw += 0.5) {
      for (let x0 = 0; x0 + cw * sec.per < w - 1; x0++) {
        let sc = 0
        for (let i = 0; i <= sec.per; i++) sc += colScore[Math.round(x0 + i * cw)]
        if (sc > best) { best = sc; left = x0; colW = cw }
      }
    }
    // từng vạch (lấy max quanh vị trí lưới vì giấy cong làm vạch lệch vài px)
    const near = (x: number) => { let m = 0; const r = Math.max(3, Math.round(colW * 0.06)); for (let k = Math.round(x) - r; k <= Math.round(x) + r; k++) if (k >= 0 && k < w && colScore[k] > m) m = colScore[k]; return m }
    const strongLines = Array.from({ length: sec.per + 1 }, (_, i) => near(left + i * colW)).filter((v) => v >= 0.25).length
    // ≥ 70% số vạch phải rõ: bảng 4 cột không khớp nhầm được lưới 6 cột (chỉ 3/7 vạch trùng)
    if (best / (sec.per + 1) < 0.35 || strongLines < Math.ceil((sec.per + 1) * 0.7)) {
      lastSheetFailure = `bảng ${sec.group}: không thấy đủ vạch dọc (${(best / (sec.per + 1)).toFixed(2)})`
      return null
    }
    const edges = Array.from({ length: sec.per + 1 }, (_, i) => left + i * colW)
    for (let i = 0; i < sec.count; i++) {
      const r = Math.floor(i / sec.per), c = i % sec.per
      const numH = L[2 * r + 1] - L[2 * r]
      const cx0 = Math.round(edges[c] + colW * 0.15), cx1 = Math.round(edges[c + 1] - colW * 0.15)
      const top = refineY(L[2 * r + 1], cx0, cx1, numH * 0.4)
      const bot = refineY(L[2 * r + 2], cx0, cx1, numH * 0.4)
      const ry0 = Math.round(top + (bot - top) * 0.2), ry1 = Math.round(bot - (bot - top) * 0.2)
      const lx = refineX(edges[c], ry0, ry1, colW * 0.12), rx = refineX(edges[c + 1], ry0, ry1, colW * 0.12)
      const dx = Math.max(3, (rx - lx) * 0.08), dy = Math.max(3, (bot - top) * 0.13)
      const bx0 = Math.round(lx + dx), bx1 = Math.round(rx - dx), by0 = Math.round(top + dy), by1 = Math.round(bot - dy)
      if (bx1 <= bx0 || by1 <= by0) { lastSheetFailure = `bảng ${sec.group}: ô ${i + 1} lệch`; return null }
      // Ô TRỐNG khi không có điểm nào tối HẲN so với nền giấy trong chính ô đó (so phân vị 2% với trung vị,
      // không dùng ngưỡng cố định → ảnh xám / mực nhạt vẫn nhận ra có nét). Nghi ngờ thì coi là có chữ.
      const hist = new Uint32Array(256)
      let cnt = 0
      for (let y = by0; y < by1; y++) for (let x = bx0; x < bx1; x++) { hist[g[y * w + x]]++; cnt++ }
      const pct = (q: number) => { let acc = 0; for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= q * cnt) return v } return 255 }
      const p50 = pct(0.5), p2 = pct(0.02)
      const blank = p50 - p2 < Math.max(16, p50 * 0.09)
      out.push({
        group: sec.group, index: i + 1, blank,
        canvas: crop(canvas, bx0 / scale, by0 / scale, (bx1 - bx0) / scale, (by1 - by0) / scale),
      })
    }
    return out
  }
}

/** Ảnh phiếu đã nắn của lần đọc gần nhất (để hiện cho học sinh đối chiếu). */
export let lastWarped: HTMLCanvasElement | null = null

/** Ảnh phiếu (đã chụp) → các ô đáp án theo thứ tự trong phần; không tìm được 4 ô đen / bảng → null. */
export function extractSheetCells(photo: HTMLCanvasElement, sections: SheetSection[]): SheetCell[] | null {
  const quad = findMarkerQuad(photo)
  if (!quad) { lastSheetFailure = 'không thấy đủ 4 ô đen'; return null }
  // Phiếu Word/PDF: ô đen nằm SÁT mép bảng → nắn rộng hơn tâm 4 ô đen ~6% để giữ trọn viền bảng
  const warped = warpQuad(photo, expandQuad(quad, 0.06, false))
  // Thử thêm chiều lật ngược (chụp ngược đầu phiếu)
  const upright = cellsOf(warped, sections)
  if (upright) { lastWarped = warped; return upright }
  const why = lastSheetFailure
  const turned = rotate90(warped, 2)
  const flipped = cellsOf(turned, sections)
  if (!flipped) lastSheetFailure = `${why} | lật ngược: ${lastSheetFailure}`
  else lastWarped = turned
  return flipped
}
